/**
 * Image commercial pricing — the registry, its credit math, and provider-cost lookup.
 * Import from `./pricing` (the barrel) unless you specifically want only image.
 */
import type { ImageGenerationModelType } from "../../image/enums"
import {
  EByteDanceImageGenerationModelType,
  FluxImageGenerationModelType,
  GoogleImageGenerationModelType,
  GrokImageGenerationModelType,
  HiDreamImageGenerationModelType,
  IdeogramImageGenerationModelType,
  EMaiImageGenerationModelType,
  EMossImageGenerationModelType,
  EKreaImageGenerationModelType,
  MinimaxImageGenerationModelType,
  NucleusImageGenerationModelType,
  NvidiaImageGenerationModelType,
  OpenAIImageGenerationModelType,
  PrunaAIImageGenerationModelType,
  QwenImageGenerationModelType,
  RecraftImageGenerationModelType,
  EReveImageGenerationModelType,
  WanImageGenerationModelType,
  normalizeImageModelId,
} from "../../image/enums"
import { extractReferenceMedia } from "../reference/routes"
import { blg } from "../../logging"
import type { ImageGenerationModelParams } from "./schemas/image"
import { krea3ObjectSchema } from "./schemas/image/krea"
import { QWEN_IMAGE_3_1K_SIZES } from "./schemas/image/qwen"
import { CONSUMER_MARKUP_MULTIPLIER, EPricingUnit, convertUsdToCredits } from "./pricing.shared"
import type { ImageModelPricing } from "./pricing.shared"

// WaveSpeed Krea: USD0.03 per 1024² output pixels, plus each delivered reference.
const getKreaWaveRate = (params: ImageGenerationModelParams, edit = false): number => {
  const resolution = krea3ObjectSchema.shape.resolution.parse("resolution" in params ? params.resolution : undefined)
  const [width = 1024, height = 1024] = resolution.split("*").map(Number)
  // The edit contract requires at least one reference; use that minimum for static quotes.
  const referenceCount = edit
    ? Math.max(1, extractReferenceMedia(EKreaImageGenerationModelType.KREA_3_EDIT, params).images?.length ?? 0)
    : 0
  // Charge each reference at OUTPUT size and round the TOTAL up to microUSD.
  return Math.ceil(((30000 * width * height) / 1048576) * (1 + referenceCount)) / 1000000
}
const getKreaEditWaveRate = (params: ImageGenerationModelParams): number => getKreaWaveRate(params, true)

// WaveSpeed MOSS: published 2026-10-19; flat per output, independent of aspect/format.
const getMossWaveRate = (params: ImageGenerationModelParams): number => {
  if ("resolution" in params && params.resolution === "2k") return 0.08
  if ("resolution" in params && params.resolution === "1.5k") return 0.04
  return 0.02
}

const getNanoBanana21ReplicateRate = (params: ImageGenerationModelParams): number => {
  if ("resolution" in params && params.resolution === "4K") return 0.1134
  if ("resolution" in params && params.resolution === "2K") return 0.0504
  return 0.0336
}

const NANO_BANANA_21_WAVE_REFERENCE_COST_USD = 0.002
const getNanoBanana21WaveRate = (params: ImageGenerationModelParams): number => {
  const resolution = "resolution" in params ? params.resolution : "1K"
  let baseRate = 0.04
  if (resolution === "4K") baseRate = 0.135
  else if (resolution === "2K") baseRate = 0.06
  const referenceCount =
    extractReferenceMedia(GoogleImageGenerationModelType.NANO_BANANA_2_1, params).images?.length ?? 0
  return baseRate + NANO_BANANA_21_WAVE_REFERENCE_COST_USD * referenceCount
}
const getNanoBanana21KieRate = (params: ImageGenerationModelParams): number => {
  if ("resolution" in params && params.resolution === "4K") return 0.045
  if ("resolution" in params && params.resolution === "2K") return 0.03
  return 0.02
}
// Kie is dev-only. FAL remains unregistered until its billing floor is qualified.
const getNanoBanana21Rate = (params: ImageGenerationModelParams): number =>
  Math.max(getNanoBanana21ReplicateRate(params), getNanoBanana21WaveRate(params))

const getZImageReplicateCost = (params: ImageGenerationModelParams): number => {
  const width = "width" in params ? Number(params.width) : 512
  const height = "height" in params ? Number(params.height) : 896
  const pixels = width * height
  if (pixels <= 500_000) return 0.0025
  if (pixels <= 1_000_000) return 0.005
  if (pixels <= 2_000_000) return 0.01
  return 0.025
}

/**
 * fal-ai/nano-banana-2 (the slug behind Nano Banana 2) bills a $0.08 base with resolution
 * multipliers — 2K ×1.5, 4K ×2 — per the live FAL catalog (https://fal.ai/api/models, 2026-07-31).
 * FAL's 0.5K ×0.75 tier isn't exposed by our schema. Web search (+$0.015) is never enabled.
 */
const getNanoBanana2FalRate = (params: ImageGenerationModelParams): number => {
  const resolution = "resolution" in params ? String(params.resolution) : "1K"
  if (resolution === "4K") return 0.16
  if (resolution === "2K") return 0.12
  return 0.08
}

const getIdeogramV4FalRate = (params: ImageGenerationModelParams): number => {
  return "rendering_speed" in params && params.rendering_speed === "QUALITY" ? 0.025 : 0.015
}

const calculateIdeogramV4FalCost = (rate: number, params: ImageGenerationModelParams): number => {
  const resolution = "resolution" in params ? String(params.resolution) : "2048x2048"
  const [width = 2048, height = 2048] = resolution.split("x").map(Number)
  return rate * ((width * height) / (1024 * 1024))
}

const getIdeogramV4Rate = (params: ImageGenerationModelParams): number => {
  return "rendering_speed" in params && params.rendering_speed === "QUALITY" ? 0.1 : 0.06
}

/**
 * Grok Imagine Image 2.0 (FAL only — VM-1054, verified 2026-08-12).
 *
 * FAL bills per OUTPUT image on a resolution × quality grid, and the edit endpoint adds a flat
 * surcharge per INPUT image:
 *
 *   1k/low $0.04 · 1k/medium $0.06 · 2k/low $0.06 · 2k/medium $0.08 · edit +$0.01 per reference
 *
 * A flat rate cannot express that, so both the consumer rate and the FAL provider cost are
 * functions and `calculateCost` folds in the output count plus the reference surcharge. Omitting
 * the surcharge would under-bill every multi-reference edit.
 */
const GROK_2_REFERENCE_SURCHARGE_USD = 0.01

// Consumer rate = provider cost × 5/3, matching the corrected Grok Aurora entries below: after the
// 1.2× markup and credit rounding that lands at ~50% gross margin, and it clears the pricing floor
// (rate >= providerCost) at every tier.
const GROK_2_MARGIN_MULTIPLIER = 5 / 3

const getGrokImagine2FalRate = (params: ImageGenerationModelParams): number => {
  const resolution = "resolution" in params ? String(params.resolution) : "1k"
  const quality = "quality" in params ? String(params.quality) : "medium"
  if (resolution === "2k") return quality === "low" ? 0.06 : 0.08
  return quality === "low" ? 0.04 : 0.06
}

const getGrokImagine2Rate = (params: ImageGenerationModelParams): number =>
  getGrokImagine2FalRate(params) * GROK_2_MARGIN_MULTIPLIER

const countGrokImagine2References = (params: ImageGenerationModelParams): number =>
  "image_urls" in params && Array.isArray(params.image_urls) ? params.image_urls.length : 0

/**
 * One output image per request: VidMax's production image path is async
 * (`helpers.ts` → `runner.submit`), and `falImageRunner.submit` hardcodes
 * `buildFalInput(params, 1)`. So the bill is one rated image plus the per-reference surcharge —
 * multiplying by a requested count would charge for images the provider is never asked to make.
 * `num_images` is deliberately absent from the Grok 2.0 schemas for the same reason.
 */
const calculateGrokImagine2Cost = (
  rate: number,
  params: ImageGenerationModelParams,
  surchargePerReference: number,
): number => rate + surchargePerReference * countGrokImagine2References(params)

/**
 * Qwen Image 3 (FAL only — VM-1053, verified 2026-08-12).
 *
 * FAL bills $0.04/image at 1K and $0.075/image at 2K. Its `image_size` accepts free-form
 * dimensions and FAL never documents the 1K/2K boundary, so the SCHEMA constrains the exposed
 * domain to a closed enum whose every member sits unambiguously in one tier (see
 * `schemas/image/qwen.ts`). That makes this lookup total: any value reaching here is priced
 * exactly, and 2K is reachable only from the text-to-image schema.
 *
 * The fallback is the 2K rate, not the 1K rate: if an unrecognised size ever reaches this
 * function, over-billing ourselves is recoverable while under-billing silently loses money.
 */
const getQwenImage3FalRate = (params: ImageGenerationModelParams): number => {
  const imageSize = "image_size" in params ? String(params.image_size) : ""
  return QWEN_IMAGE_3_1K_SIZES.includes(imageSize) ? 0.04 : 0.075
}

// Consumer rate = provider cost x 5/3, matching the Grok entries: after the 1.2x markup and
// credit rounding that lands at ~50% gross margin and clears the pricing floor at both tiers.
const getQwenImage3Rate = (params: ImageGenerationModelParams): number => getQwenImage3FalRate(params) * (5 / 3)

const calculatePerImage = (rate: number, params: ImageGenerationModelParams): number => {
  const numImages =
    "num_outputs" in params && params.num_outputs
      ? params.num_outputs
      : "number_of_images" in params && params.number_of_images
        ? params.number_of_images
        : 1
  return rate * numImages
}

const getSeedream5ProRate = (params: ImageGenerationModelParams): number => {
  return "quality" in params && params.quality === "high" ? 0.135 : 0.0675
}

const getSeedream5ProKieRate = (params: ImageGenerationModelParams): number => {
  return "quality" in params && params.quality === "high" ? 0.07 : 0.035
}

const getSeedream5ProReplicateRate = (params: ImageGenerationModelParams): number => {
  return "quality" in params && params.quality === "high" ? 0.09 : 0.045
}

const getSeedream5ProEditRate = (params: ImageGenerationModelParams): number => {
  const baseRate = "quality" in params && params.quality === "high" ? 0.135 : 0.0675
  const inputImageCount = "image_urls" in params && Array.isArray(params.image_urls) ? params.image_urls.length : 1
  return baseRate + Math.max(0, inputImageCount - 1) * 0.0045
}

// GPT Image 2 provider costs — each provider bills on a DIFFERENT axis, verified against source
// (2026-07-29):
//   - kie-ai: by RESOLUTION only (1K/2K/4K).
//   - FAL: by SIZE × QUALITY (OpenAI passthrough). https://fal.ai/models/openai/gpt-image-2 (+/edit).
//     Per resolution tier we take the DEAREST aspect-ratio size so we never under-bill.
//   - Replicate: FLAT by QUALITY, no size scaling. https://replicate.com/openai/gpt-image-2
// The consumer rate must clear the dearest routable provider for the given (resolution, quality) —
// at 4K high that is FAL ($0.401 T2I / $0.413 edit). Quality is optional in the schema and
// defaults to "auto" ≈ high (the dearest), so a missing quality maps to the high tier.
type GptImage2Resolution = "1K" | "2K" | "4K"
type GptImage2QualityTier = "high" | "low" | "medium"
const gptImage2Resolution = (params: ImageGenerationModelParams): GptImage2Resolution => {
  const resolution = "resolution" in params ? String(params.resolution) : "4K"
  if (resolution === "1K") return "1K"
  if (resolution === "2K") return "2K"
  return "4K" // 4K default (schema default)
}
// auto / unset → high (dearest); GPT Image 2 has no separate "auto" price row.
const gptImage2QualityTier = (params: ImageGenerationModelParams): GptImage2QualityTier => {
  const quality = "quality" in params ? params.quality : undefined
  if (quality === "low") return "low"
  if (quality === "medium") return "medium"
  return "high"
}
const getGptImage2ResolutionRate = (params: ImageGenerationModelParams): number => {
  const resolution = gptImage2Resolution(params)
  if (resolution === "1K") return 0.03
  if (resolution === "2K") return 0.05
  return 0.08 // 4K
}
// FAL T2I, dearest size within each resolution tier × quality. https://fal.ai/models/openai/gpt-image-2
const GPT_IMAGE_2_FAL_T2I: Record<GptImage2Resolution, Record<GptImage2QualityTier, number>> = {
  "1K": { low: 0.006, medium: 0.053, high: 0.211 },
  "2K": { low: 0.007, medium: 0.056, high: 0.222 },
  "4K": { low: 0.012, medium: 0.101, high: 0.401 },
}
// FAL edit. https://fal.ai/models/openai/gpt-image-2/edit
const GPT_IMAGE_2_FAL_EDIT: Record<GptImage2Resolution, Record<GptImage2QualityTier, number>> = {
  "1K": { low: 0.018, medium: 0.061, high: 0.219 },
  "2K": { low: 0.019, medium: 0.068, high: 0.234 },
  "4K": { low: 0.024, medium: 0.113, high: 0.413 },
}
const getGptImage2FalT2IRate = (params: ImageGenerationModelParams): number =>
  GPT_IMAGE_2_FAL_T2I[gptImage2Resolution(params)][gptImage2QualityTier(params)]
const getGptImage2FalEditRate = (params: ImageGenerationModelParams): number =>
  GPT_IMAGE_2_FAL_EDIT[gptImage2Resolution(params)][gptImage2QualityTier(params)]
// Replicate: flat per-image by quality (same slug for T2I + edit). https://replicate.com/openai/gpt-image-2
const getGptImage2ReplicateRate = (params: ImageGenerationModelParams): number => {
  const tier = gptImage2QualityTier(params)
  if (tier === "low") return 0.012
  if (tier === "medium") return 0.047
  return 0.128 // high / auto
}
// Consumer rate = dearest routable provider for this (resolution, quality) so the 1.2x markup is
// real margin whichever route is selected. Binding case: 4K high → FAL $0.401 → customer ~$0.48.
const getGptImage2T2IRate = (params: ImageGenerationModelParams): number =>
  Math.max(getGptImage2ResolutionRate(params), getGptImage2FalT2IRate(params), getGptImage2ReplicateRate(params))
const getGptImage2EditRate = (params: ImageGenerationModelParams): number =>
  Math.max(getGptImage2ResolutionRate(params), getGptImage2FalEditRate(params), getGptImage2ReplicateRate(params))

export const replicateImageModelPricing: Partial<Record<ImageGenerationModelType, ImageModelPricing>> = {
  // Flux models
  [FluxImageGenerationModelType.FLUX_SCHNELL]: {
    rate: 0.003,
    unit: EPricingUnit.PER_IMAGE,
    description: "Flux Schnell: $0.003 per image ($3 per 1000 images)",
    providerCosts: {
      fal: { rate: 0.003, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.003/image" },
      replicate: { rate: 0.003, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.003/image" },
    },
  },
  [FluxImageGenerationModelType.FLUX_DEV]: {
    rate: 0.025,
    unit: EPricingUnit.PER_IMAGE,
    description: "Flux Dev: $0.025 per output image",
    providerCosts: {
      fal: { rate: 0.025, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.025/image" },
      replicate: { rate: 0.025, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.025/image" },
    },
  },
  [FluxImageGenerationModelType.FLUX_PRO]: {
    rate: 0.055,
    unit: EPricingUnit.PER_IMAGE,
    description: "Flux Pro: $0.055 per output image",
    providerCosts: {
      fal: { rate: 0.055, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.055/image" },
      replicate: { rate: 0.055, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.055/image" },
    },
  },
  [FluxImageGenerationModelType.FLUX_2_PRO]: {
    rate: 0.05,
    unit: EPricingUnit.PER_IMAGE,
    description: "Flux 2 Pro: $0.05 per output image (FAL)",
    providerCosts: {
      fal: { rate: 0.05, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.05/image" },
    },
  },

  // Google models
  [GoogleImageGenerationModelType.IMAGEN_4]: {
    rate: 0.04,
    unit: EPricingUnit.PER_IMAGE,
    description: "Google Imagen 4: $0.04 per output image",
    providerCosts: {
      replicate: { rate: 0.04, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.04/image" },
    },
  },
  [GoogleImageGenerationModelType.IMAGEN_4_FAST]: {
    rate: 0.02,
    unit: EPricingUnit.PER_IMAGE,
    description: "Google Imagen 4 Fast: $0.02 per output image",
    providerCosts: {
      replicate: { rate: 0.02, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.02/image" },
    },
  },
  [GoogleImageGenerationModelType.IMAGEN_4_ULTRA]: {
    rate: 0.06,
    unit: EPricingUnit.PER_IMAGE,
    description: "Google Imagen 4 Ultra: $0.06 per output image",
    providerCosts: {
      replicate: { rate: 0.06, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.06/image" },
    },
  },
  [GoogleImageGenerationModelType.NANO_BANANA]: {
    rate: 0.039,
    unit: EPricingUnit.PER_IMAGE,
    description: "Google Nano Banana: $0.039 per output image",
    providerCosts: {
      replicate: { rate: 0.039, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.039/image" },
      // Routable to fal + kie-ai (MODEL_PROVIDERS) — mirror canonical rate so cost capture isn't NULL.
      fal: { rate: 0.039, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.039/image" },
      // Kie.ai real per-image is $0.02 (pricing-audit, creditUnit "per image") — corrected from the
      // mirrored $0.039. Telemetry-only; consumer rate unchanged (already profitable).
      "kie-ai": { rate: 0.02, unit: EPricingUnit.PER_IMAGE, description: "Kie.ai: $0.02/image" },
    },
  },
  [GoogleImageGenerationModelType.NANO_BANANA_PRO]: {
    rate: (params) => {
      const resolution = "resolution" in params ? params.resolution : "2K"
      return resolution === "4K" ? 0.3 : 0.15
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Google Nano Banana Pro: $0.15/image (1K/2K), $0.30/image (4K)",
    providerCosts: {
      replicate: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "2K"
          return resolution === "4K" ? 0.3 : 0.15
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.15/image (1K/2K), $0.30/image (4K)",
      },
      // Routable to fal + kie-ai (MODEL_PROVIDERS) — mirror the resolution-tiered canonical rate.
      fal: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "2K"
          return resolution === "4K" ? 0.3 : 0.15
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.15/image (1K/2K), $0.30/image (4K)",
      },
      // Kie.ai real (pricing-audit): $0.09 (1K/2K), $0.12 (4K) — corrected from the mirrored 0.15/0.30.
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "2K"
          return resolution === "4K" ? 0.12 : 0.09
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.09/image (1K/2K), $0.12/image (4K)",
      },
      openrouter: {
        rate: (params) => {
          const resolution = "resolution" in params ? params.resolution : "2K"
          return resolution === "4K" ? 0.3 : 0.15
        },
        unit: EPricingUnit.PER_IMAGE,
        description:
          "OpenRouter fallback estimate: dearest-provider floor $0.15/image (1K/2K), $0.30/image (4K); runtime usage.cost is preferred",
      },
    },
  },
  // Consumer rate tracks FAL, the primary production route for this model (image/registry.ts), because
  // the pricing floor is the dearest routable provider. The previous flat $0.09 (1K/2K) / $0.12 (4K)
  // was sized off Kie.ai list price and billed UNDER FAL cost at 2K ($0.09 vs $0.12) and 4K ($0.12 vs
  // $0.16) — every 2K/4K generation on the primary route lost money even after the consumer markup.
  [GoogleImageGenerationModelType.NANO_BANANA_2_1]: {
    rate: getNanoBanana21Rate,
    unit: EPricingUnit.PER_IMAGE,
    description: "Nano Banana 2.1 production floor: $0.04/$0.06/$0.135 by resolution plus $0.002 per edit reference",
    providerCosts: {
      replicate: {
        rate: getNanoBanana21ReplicateRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate official tariff: $0.0336/$0.0504/$0.1134 per output image",
      },
      wavespeed: {
        rate: getNanoBanana21WaveRate,
        unit: EPricingUnit.PER_IMAGE,
        description:
          "WaveSpeed published tariff: $0.04/$0.06/$0.135 per output + $0.002 per edit reference; final billing is separate",
      },
      "kie-ai": {
        rate: getNanoBanana21KieRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie dev-only: $0.02/$0.03/$0.045 per output image",
      },
    },
  },
  [GoogleImageGenerationModelType.NANO_BANANA_2]: {
    rate: getNanoBanana2FalRate,
    unit: EPricingUnit.PER_IMAGE,
    description: "Nano Banana 2: $0.08/image (1K), $0.12/image (2K), $0.16/image (4K)",
    providerCosts: {
      // Kie.ai real (pricing-audit): $0.04 (1K), $0.06 (2K), $0.09 (4K) — corrected from 0.09/0.12.
      "kie-ai": {
        rate: (params) => {
          const resolution = "resolution" in params ? String(params.resolution) : "1K"
          if (resolution === "4K") return 0.09
          if (resolution === "2K") return 0.06
          return 0.04
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.04/image (1K), $0.06/image (2K), $0.09/image (4K)",
      },
      fal: {
        rate: getNanoBanana2FalRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.08/image (1K), $0.12/image (2K), $0.16/image (4K)",
      },
      openrouter: {
        rate: getNanoBanana2FalRate,
        unit: EPricingUnit.PER_IMAGE,
        description:
          "OpenRouter fallback estimate: dearest-provider floor $0.08/$0.12/$0.16 per image by 1K/2K/4K; runtime usage.cost is preferred",
      },
    },
  },
  [GoogleImageGenerationModelType.GEMINI_2_5_FLASH_IMAGE]: {
    rate: 0.039,
    unit: EPricingUnit.PER_IMAGE,
    description: "Google Gemini 2.5 Flash Image: $0.039 per output image",
    providerCosts: {
      replicate: { rate: 0.039, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.039/image" },
    },
  },

  // Nvidia models
  [NvidiaImageGenerationModelType.SANA]: {
    rate: 0.05,
    unit: EPricingUnit.PER_IMAGE,
    description: "Nvidia SANA: $0.05 per image (p50 price)",
    providerCosts: {
      replicate: { rate: 0.05, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.05/image" },
    },
  },

  // Minimax models
  [MinimaxImageGenerationModelType.MINIMAX_IMAGE_01]: {
    rate: 0.01,
    unit: EPricingUnit.PER_IMAGE,
    description: "Minimax Image-01: $0.01 per output image",
    providerCosts: {
      replicate: { rate: 0.01, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.01/image" },
    },
  },

  // ByteDance models
  [EByteDanceImageGenerationModelType.SEEDREAM_4]: {
    rate: 0.03,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance Seedream-4: $0.03 per output image",
    calculateCost: (rate, params) => {
      const maxImages = "max_images" in params && params.max_images ? params.max_images : 1
      return rate * maxImages
    },
    providerCosts: {
      fal: { rate: 0.03, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.03/image" },
      // Primary route is replicate (MODEL_PROVIDERS) — mirror canonical rate so cost capture isn't NULL.
      replicate: { rate: 0.03, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.03/image" },
    },
  },
  [EByteDanceImageGenerationModelType.SEEDREAM_4_5]: {
    rate: 0.04,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance Seedream-4.5: $0.04 per output image",
    calculateCost: (rate, params) => {
      const maxImages = "max_images" in params && params.max_images ? params.max_images : 1
      return rate * maxImages
    },
    providerCosts: {
      fal: { rate: 0.04, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.04/image" },
      // Primary route is replicate (MODEL_PROVIDERS) — mirror canonical rate so cost capture isn't NULL.
      replicate: { rate: 0.04, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.04/image" },
    },
  },

  // ByteDance SeedDream 4.5 Edit — Kie primary, FAL + Replicate fallbacks
  [EByteDanceImageGenerationModelType.SEEDREAM_4_5_EDIT]: {
    // Sized to the dearest routable provider (FAL/Replicate $0.04) so the 1.2x markup is real
    // margin. Kie.ai ($0.0325) is the cheaper primary route and earns extra headroom.
    rate: 0.04,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance SeedDream 4.5 Edit: $0.04/image (floor = FAL/Replicate $0.04; Kie.ai $0.0325 primary)",
    providerCosts: {
      "kie-ai": {
        rate: 0.0325,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0325/image (19% cheaper than FAL $0.04)",
      },
      fal: { rate: 0.04, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.04/image" },
      replicate: { rate: 0.04, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.04/image" },
    },
  },

  // ByteDance SeedDream 5 Lite T2I — Kie primary, FAL + Replicate fallbacks
  [EByteDanceImageGenerationModelType.SEEDREAM_5_LITE]: {
    rate: 0.035,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance SeedDream 5 Lite T2I: $0.035/image",
    providerCosts: {
      "kie-ai": {
        rate: 0.0275,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0275/image (21% cheaper than FAL $0.035)",
      },
      fal: { rate: 0.035, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.035/image" },
      replicate: { rate: 0.035, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.035/image" },
    },
  },

  // ByteDance SeedDream 5 Lite I2I — Kie primary, FAL + Replicate fallbacks
  [EByteDanceImageGenerationModelType.SEEDREAM_5_LITE_EDIT]: {
    rate: 0.035,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance SeedDream 5 Lite I2I: $0.035/image",
    providerCosts: {
      "kie-ai": {
        rate: 0.0275,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0275/image (21% cheaper than FAL $0.035)",
      },
      fal: { rate: 0.035, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.035/image" },
      replicate: { rate: 0.035, unit: EPricingUnit.PER_IMAGE, description: "Replicate: $0.035/image" },
    },
  },

  // ByteDance SeedDream 5 Pro T2I — FAL primary, Kie.ai + Replicate fallback
  [EByteDanceImageGenerationModelType.SEEDREAM_5_PRO]: {
    rate: getSeedream5ProRate,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance SeedDream 5 Pro T2I on FAL: $0.0675/image (1K) or $0.135/image (2K)",
    providerCosts: {
      fal: {
        rate: getSeedream5ProRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.0675/image (1K) or $0.135/image (2K)",
      },
      "kie-ai": {
        rate: getSeedream5ProKieRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.035/image (1K) or $0.07/image (2K)",
      },
      replicate: {
        rate: getSeedream5ProReplicateRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.045/image (1K) or $0.09/image (2K)",
      },
    },
  },

  // ByteDance SeedDream 5 Pro Edit — first input free, each additional input costs $0.0045
  [EByteDanceImageGenerationModelType.SEEDREAM_5_PRO_EDIT]: {
    rate: getSeedream5ProEditRate,
    unit: EPricingUnit.PER_IMAGE,
    description: "ByteDance SeedDream 5 Pro Edit on FAL: $0.0675 (1K) / $0.135 (2K) + $0.0045/additional input",
    providerCosts: {
      fal: {
        rate: getSeedream5ProEditRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.0675 (1K) / $0.135 (2K) + $0.0045/additional input",
      },
    },
  },

  // OpenAI Image models (via Replicate with our OPENAI_API_KEY passthrough → OpenAI direct billing)
  // DALL-E 3 routes to the "openai" provider (MODEL_PROVIDERS) — bill OpenAI's published per-image rate.
  // 1024² standard $0.04, HD (or non-square) $0.08. https://openai.com/api/pricing
  [OpenAIImageGenerationModelType.DALLE_3]: {
    rate: (params) => ("quality" in params && params.quality === "hd" ? 0.08 : 0.04),
    unit: EPricingUnit.PER_IMAGE,
    description: "DALL-E 3: $0.04/image (standard 1024²), $0.08/image (HD)",
    providerCosts: {
      openai: {
        rate: (params) => ("quality" in params && params.quality === "hd" ? 0.08 : 0.04),
        unit: EPricingUnit.PER_IMAGE,
        description: "OpenAI: $0.04 (standard), $0.08 (HD) per image",
      },
    },
  },
  [OpenAIImageGenerationModelType.GPT_IMAGE]: {
    // rate = OpenAI cost × 5/3 so the 1.2× markup yields a 50% gross margin (customer = 2 × cost).
    rate: (params) => {
      const quality = "quality" in params ? params.quality : "auto"
      if (quality === "low") return 0.0183
      if (quality === "medium") return 0.07
      return 0.2783 // high / auto — OpenAI bills auto at the high rate
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "GPT Image 1: rate 0.0183/0.07/0.2783 (low/med/high·auto) → 50% GM on OpenAI cost 0.011/0.042/0.167",
    calculateCost: (rate, params) => {
      const numImages = "number_of_images" in params && params.number_of_images ? params.number_of_images : 1
      return rate * numImages
    },
    providerCosts: {
      replicate: {
        rate: (params) => {
          const quality = "quality" in params ? params.quality : "auto"
          if (quality === "low") return 0.011
          if (quality === "medium") return 0.042
          return 0.167 // high / auto — OpenAI bills auto at the high rate
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate (passthrough → OpenAI direct): $0.011-$0.167/image",
        calculateCost: (rate, params) => {
          const numImages = "number_of_images" in params && params.number_of_images ? params.number_of_images : 1
          return rate * numImages
        },
      },
    },
  },
  [OpenAIImageGenerationModelType.GPT_IMAGE_1_5]: {
    // rate = replicate cost × 5/3 so the 1.2× markup yields a 50% gross margin (customer = 2 × cost).
    rate: (params) => {
      const quality = "quality" in params ? params.quality : "auto"
      if (quality === "low") return 0.0217
      if (quality === "medium") return 0.0833
      return 0.2267 // high / auto — replicate bills auto at the high rate
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "GPT Image 1.5: rate 0.0217/0.0833/0.2267 (low/med/high·auto) → 50% GM on cost 0.013/0.05/0.136",
    calculateCost: (rate, params) => {
      const numImages = "number_of_images" in params && params.number_of_images ? params.number_of_images : 1
      return rate * numImages
    },
    providerCosts: {
      replicate: {
        rate: (params) => {
          const quality = "quality" in params ? params.quality : "auto"
          if (quality === "low") return 0.013
          if (quality === "medium") return 0.05
          return 0.136 // high / auto — replicate bills auto at the high rate
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.013 (low) / $0.05 (medium) / $0.136 (high·auto) per image",
        calculateCost: (rate, params) => {
          const numImages = "number_of_images" in params && params.number_of_images ? params.number_of_images : 1
          return rate * numImages
        },
      },
    },
  },
  [OpenAIImageGenerationModelType.GPT_IMAGE_1_MINI]: {
    rate: 0.005,
    unit: EPricingUnit.PER_IMAGE,
    description: "GPT Image 1 Mini: $0.005 per output image",
    calculateCost: (rate, params) => {
      const numImages = "number_of_images" in params && params.number_of_images ? params.number_of_images : 1
      return rate * numImages
    },
    providerCosts: {
      replicate: {
        rate: 0.005,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.005/image",
        calculateCost: (rate, params) => {
          const numImages = "number_of_images" in params && params.number_of_images ? params.number_of_images : 1
          return rate * numImages
        },
      },
    },
  },

  // Flux 2 Flex (FAL)
  [FluxImageGenerationModelType.FLUX_2_FLEX]: {
    rate: 0.05,
    unit: EPricingUnit.PER_IMAGE,
    description: "Flux 2 Flex: $0.05/megapixel (style transfer)",
    providerCosts: {
      fal: { rate: 0.05, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.05/image" },
    },
  },
  // Flux Pro Kontext (FAL)
  [FluxImageGenerationModelType.FLUX_PRO_KONTEXT]: {
    rate: 0.05,
    unit: EPricingUnit.PER_IMAGE,
    description: "Flux Pro Kontext: $0.05/image (context/reference)",
    providerCosts: {
      fal: { rate: 0.05, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.05/image" },
    },
  },
  // Recraft V4 Pro (FAL)
  [RecraftImageGenerationModelType.RECRAFT_V4_PRO]: {
    rate: 0.25,
    unit: EPricingUnit.PER_IMAGE,
    description: "Recraft V4 Pro: $0.25/image (premium quality)",
    providerCosts: {
      fal: { rate: 0.25, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.25/image" },
    },
  },
  // Qwen Image (FAL)
  [QwenImageGenerationModelType.QWEN_IMAGE]: {
    rate: 0.02,
    unit: EPricingUnit.PER_IMAGE,
    description: "Qwen Image: $0.02/megapixel (budget)",
    providerCosts: {
      fal: { rate: 0.02, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.02/image" },
    },
  },

  // Qwen Image 3 T2I (FAL: alibaba/qwen-image-3/text-to-image) — FAL-exclusive; Replicate has no v3.
  // One output image per request (async submit hardcodes 1), so no output-count multiplier.
  [QwenImageGenerationModelType.QWEN_IMAGE_3]: {
    rate: getQwenImage3Rate,
    unit: EPricingUnit.PER_IMAGE,
    description: "Qwen Image 3 T2I (FAL): rate $0.0667 (1K) / $0.125 (2K) — 50% GM on FAL $0.04 / $0.075",
    providerCosts: {
      fal: {
        rate: getQwenImage3FalRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.04/image at 1K, $0.075/image at 2K",
      },
    },
  },

  // Qwen Image 3 Edit (FAL: alibaba/qwen-image-3/edit) — 1-3 reference images, no per-reference
  // surcharge (unlike Grok 2.0). FAL caps edit at 1440x1440, so only the 1K tier is reachable.
  [QwenImageGenerationModelType.QWEN_IMAGE_3_EDIT]: {
    rate: getQwenImage3Rate,
    unit: EPricingUnit.PER_IMAGE,
    description: "Qwen Image 3 Edit (FAL): rate $0.0667/image — 50% GM on FAL $0.04 (1K only; edit caps at 1440x1440)",
    providerCosts: {
      fal: {
        rate: getQwenImage3FalRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.04/image at 1K",
      },
    },
  },

  // Ideogram V4 (FAL + Replicate)
  [IdeogramImageGenerationModelType.IDEOGRAM_V4]: {
    rate: getIdeogramV4Rate,
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram V4: $0.06 (balanced) / $0.10 (quality) per image",
    providerCosts: {
      fal: {
        rate: getIdeogramV4FalRate,
        unit: EPricingUnit.PER_IMAGE,
        calculateCost: calculateIdeogramV4FalCost,
        description: "FAL: $0.015/MP balanced or $0.025/MP quality",
      },
      replicate: {
        rate: getIdeogramV4Rate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.06 balanced or $0.10 quality per image",
      },
    },
  },
  [IdeogramImageGenerationModelType.IDEOGRAM_V4_EDIT]: {
    rate: getIdeogramV4Rate,
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram V4 Edit: $0.06 (balanced) / $0.10 (quality) per image",
    providerCosts: {
      fal: {
        rate: getIdeogramV4FalRate,
        unit: EPricingUnit.PER_IMAGE,
        calculateCost: calculateIdeogramV4FalCost,
        description: "FAL: $0.015/MP balanced or $0.025/MP quality",
      },
    },
  },

  // Ideogram V3 (Kie.ai) — 42-44% cheaper than FAL
  // TURBO: $0.0175/image | BALANCED: $0.035/image | QUALITY: $0.05/image
  [IdeogramImageGenerationModelType.IDEOGRAM_V3]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.0175
      if (speed === "QUALITY") return 0.05
      return 0.035 // BALANCED default
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram V3: $0.0175 (turbo) / $0.035 (balanced) / $0.05 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.0175
          if (speed === "QUALITY") return 0.05
          return 0.035
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0175-$0.05/image (42-44% cheaper than FAL)",
      },
    },
  },
  [IdeogramImageGenerationModelType.IDEOGRAM_V3_EDIT]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.0175
      if (speed === "QUALITY") return 0.05
      return 0.035
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram V3 Edit: $0.0175 (turbo) / $0.035 (balanced) / $0.05 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.0175
          if (speed === "QUALITY") return 0.05
          return 0.035
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0175-$0.05/image",
      },
    },
  },

  // Ideogram V3 Remix (Kie.ai only) — I2I remix with style+strength, 42-44% cheaper than FAL
  [IdeogramImageGenerationModelType.IDEOGRAM_V3_REMIX]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.0175
      if (speed === "QUALITY") return 0.05
      return 0.035
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram V3 Remix: $0.0175 (turbo) / $0.035 (balanced) / $0.05 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.0175
          if (speed === "QUALITY") return 0.05
          return 0.035
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0175-$0.05/image (42-44% cheaper than FAL)",
      },
    },
  },

  // Ideogram V3 Reframe (Kie.ai only) — outpainting/reframing with mask, 42-44% cheaper than FAL
  [IdeogramImageGenerationModelType.IDEOGRAM_V3_REFRAME]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.0175
      if (speed === "QUALITY") return 0.05
      return 0.035
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram V3 Reframe: $0.0175 (turbo) / $0.035 (balanced) / $0.05 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.0175
          if (speed === "QUALITY") return 0.05
          return 0.035
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.0175-$0.05/image (42-44% cheaper than FAL)",
      },
    },
  },

  // Ideogram Character T2I (Kie.ai only) — 40% cheaper than FAL, $0.06-$0.12/img
  [IdeogramImageGenerationModelType.IDEOGRAM_CHARACTER]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.06
      if (speed === "QUALITY") return 0.12
      return 0.09
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram Character: $0.06 (turbo) / $0.09 (balanced) / $0.12 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.06
          if (speed === "QUALITY") return 0.12
          return 0.09
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.06-$0.12/image (40% cheaper than FAL $0.10-$0.20)",
      },
    },
  },

  // Ideogram Character Remix (Kie.ai only) — 40% cheaper than FAL, $0.06-$0.12/img
  [IdeogramImageGenerationModelType.IDEOGRAM_CHARACTER_REMIX]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.06
      if (speed === "QUALITY") return 0.12
      return 0.09
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram Character Remix: $0.06 (turbo) / $0.09 (balanced) / $0.12 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.06
          if (speed === "QUALITY") return 0.12
          return 0.09
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.06-$0.12/image (40% cheaper than FAL $0.10-$0.20)",
      },
    },
  },

  // Ideogram Character Edit (Kie.ai only) — 40% cheaper than FAL, $0.06-$0.12/img
  [IdeogramImageGenerationModelType.IDEOGRAM_CHARACTER_EDIT]: {
    rate: (params) => {
      const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
      if (speed === "TURBO") return 0.06
      if (speed === "QUALITY") return 0.12
      return 0.09
    },
    unit: EPricingUnit.PER_IMAGE,
    description: "Ideogram Character Edit: $0.06 (turbo) / $0.09 (balanced) / $0.12 (quality) per image",
    providerCosts: {
      "kie-ai": {
        rate: (params) => {
          const speed = "rendering_speed" in params ? String(params.rendering_speed) : "BALANCED"
          if (speed === "TURBO") return 0.06
          if (speed === "QUALITY") return 0.12
          return 0.09
        },
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.06-$0.12/image (40% cheaper than FAL $0.10-$0.20)",
      },
    },
  },

  // Wan 2.7 Image (Kie.ai) — 20% cheaper than FAL ($0.03 → $0.024)
  [WanImageGenerationModelType.WAN_2_7_IMAGE]: {
    rate: 0.024,
    unit: EPricingUnit.PER_IMAGE,
    description: "Wan 2.7 Image: $0.024/image (standard, Kie.ai)",
    providerCosts: {
      "kie-ai": {
        rate: 0.024,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.024/image (20% cheaper than FAL)",
      },
    },
  },

  // Wan 2.7 Image Pro (Kie.ai) — 20% cheaper than FAL ($0.075 → $0.06)
  [WanImageGenerationModelType.WAN_2_7_IMAGE_PRO]: {
    rate: 0.06,
    unit: EPricingUnit.PER_IMAGE,
    description: "Wan 2.7 Image Pro: $0.06/image (high quality, Kie.ai)",
    providerCosts: {
      "kie-ai": { rate: 0.06, unit: EPricingUnit.PER_IMAGE, description: "Kie.ai: $0.06/image (20% cheaper than FAL)" },
    },
  },

  // HiDream O1 Image (FAL) — T2I + reference personalization, $0.01/megapixel (~$0.01 per 1024x1024)
  [HiDreamImageGenerationModelType.HIDREAM_O1]: {
    rate: 0.01,
    unit: EPricingUnit.PER_IMAGE,
    description: "HiDream O1: $0.01/megapixel (T2I + reference personalization, up to 2K)",
    providerCosts: {
      fal: { rate: 0.01, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.01/megapixel" },
    },
  },

  // Nucleus Image (FAL) — MoE diffusion T2I, $0.01/megapixel
  [NucleusImageGenerationModelType.NUCLEUS_IMAGE]: {
    rate: 0.01,
    unit: EPricingUnit.PER_IMAGE,
    description: "Nucleus Image: $0.01/megapixel (MoE diffusion, stylized/typography)",
    providerCosts: {
      fal: { rate: 0.01, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.01/megapixel" },
    },
  },

  // GPT Image 2 (Kie.ai: gpt-image-2-text-to-image). Rate clears the dearest routable provider per
  // (resolution, quality); at 4K high that is FAL $0.401 → customer ~$0.48. Source-verified 2026-07-29.
  [OpenAIImageGenerationModelType.GPT_IMAGE_2]: {
    rate: getGptImage2T2IRate,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "GPT Image 2: rate = dearest routable provider by (resolution, quality). 4K high ~$0.48 (FAL $0.401), 1K high ~$0.25; low/medium far cheaper (kie/Replicate lead)",
    providerCosts: {
      "kie-ai": {
        rate: getGptImage2ResolutionRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.03/$0.05/$0.08 per image by resolution (1K/2K/4K)",
      },
      fal: {
        rate: getGptImage2FalT2IRate,
        unit: EPricingUnit.PER_IMAGE,
        // Size × quality; dearest aspect per tier. https://fal.ai/models/openai/gpt-image-2 (2026-07-29)
        description: "FAL (OpenAI passthrough) by size×quality: high 0.211/0.222/0.401 for 1K/2K/4K",
      },
      replicate: {
        rate: getGptImage2ReplicateRate,
        unit: EPricingUnit.PER_IMAGE,
        // Flat by quality, no size scaling. https://replicate.com/openai/gpt-image-2 (2026-07-29)
        description: "Replicate: flat by quality — $0.012 (low) / $0.047 (medium) / $0.128 (high·auto) per image",
      },
      openrouter: {
        rate: getGptImage2T2IRate,
        unit: EPricingUnit.PER_IMAGE,
        description:
          "OpenRouter fallback estimate: dearest-provider floor by resolution×quality; runtime usage.cost is preferred",
      },
    },
  },
  [OpenAIImageGenerationModelType.GPT_IMAGE_2_EDIT]: {
    // Rate clears the dearest routable provider; 4K high → FAL $0.413 → customer ~$0.50.
    rate: getGptImage2EditRate,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "GPT Image 2 Edit: rate = dearest routable provider by (resolution, quality). 4K high ~$0.50 (FAL $0.413), 1K high ~$0.26; low/medium far cheaper",
    providerCosts: {
      "kie-ai": {
        rate: getGptImage2ResolutionRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.03/$0.05/$0.08 per image by resolution (1K/2K/4K)",
      },
      fal: {
        rate: getGptImage2FalEditRate,
        unit: EPricingUnit.PER_IMAGE,
        // Size × quality; dearest aspect per tier. https://fal.ai/models/openai/gpt-image-2/edit (2026-07-29)
        description: "FAL edit (OpenAI passthrough) by size×quality: high 0.219/0.234/0.413 for 1K/2K/4K",
      },
      replicate: {
        rate: getGptImage2ReplicateRate,
        unit: EPricingUnit.PER_IMAGE,
        // Flat by quality (same slug as T2I). https://replicate.com/openai/gpt-image-2 (2026-07-29)
        description: "Replicate: flat by quality — $0.012 (low) / $0.047 (medium) / $0.128 (high·auto) per image",
      },
      openrouter: {
        rate: getGptImage2EditRate,
        unit: EPricingUnit.PER_IMAGE,
        description:
          "OpenRouter fallback estimate: dearest-provider edit floor by resolution×quality; runtime usage.cost is preferred",
      },
    },
  },

  // Replicate publishes resolution tiers through 2 MP. Above that, use a conservative
  // estimate until its billing is verified; the Ad Engine canary always stays below 0.5 MP.
  [PrunaAIImageGenerationModelType.Z_IMAGE_TURBO]: {
    rate: (params) => getZImageReplicateCost(params) * (5 / 3),
    unit: EPricingUnit.PER_IMAGE,
    description: "PrunaAI Z-Image Turbo: resolution-based consumer rate",
    providerCosts: {
      replicate: {
        rate: 0.025,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.0025 <=0.5 MP, $0.005 <=1 MP, $0.01 <=2 MP; conservative $0.025 above",
        calculateCost: (_rate, params) => getZImageReplicateCost(params),
      },
    },
  },

  // Grok Aurora T2I (Kie.ai: grok-imagine/text-to-image) — xAI image gen, $0.025/4 images = $0.00625/image
  [GrokImageGenerationModelType.GROK_IMAGINE_T2I]: {
    // rate = cost × 5/3 so the 1.2× markup yields a 50% gross margin, matching GROK_IMAGINE_I2I
    // below. The consumer rate used to EQUAL the $0.00625 Kie cost, so after credit rounding the
    // customer paid $0.005 against $0.00625 of spend — the only genuine below-cost sale on a
    // production-routable path in the margin matrix.
    rate: 0.0104,
    unit: EPricingUnit.PER_IMAGE,
    description: "Grok Aurora T2I (xAI via Kie.ai): rate $0.0104 → customer ~$0.0125 (50% GM on Kie $0.00625 cost)",
    providerCosts: {
      "kie-ai": {
        rate: 0.00625,
        unit: EPricingUnit.PER_IMAGE,
        description: "Kie.ai: $0.025 per 4 images ($0.00625 each)",
      },
    },
  },

  // Grok Aurora I2I (Kie.ai: grok-imagine/image-to-image) — xAI image editing. Kie bills $0.02 PER
  // IMAGE (creditUnit "per image"), not per 2 — corrected from the pricing-audit (was $0.01, a loss).
  [GrokImageGenerationModelType.GROK_IMAGINE_I2I]: {
    // rate = cost × 5/3 so the 1.2× markup yields a 50% gross margin (customer = 2 × $0.02 = $0.04).
    rate: 0.0333,
    unit: EPricingUnit.PER_IMAGE,
    description: "Grok Aurora I2I (xAI via Kie.ai): rate $0.0333 → customer ~$0.04 (50% GM on Kie $0.02 cost)",
    providerCosts: {
      "kie-ai": { rate: 0.02, unit: EPricingUnit.PER_IMAGE, description: "Kie.ai: $0.02/image" },
    },
  },

  // Grok Imagine Image 2.0 T2I (FAL: xai/grok-imagine-image/v2.0/text-to-image) — FAL-exclusive.
  // Replicate/Kie.ai/WaveSpeed carry only unversioned 1.x, so FAL is the sole routable provider
  // and the top-level rate is sized to it.
  [GrokImageGenerationModelType.GROK_IMAGINE_2_T2I]: {
    rate: getGrokImagine2Rate,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "Grok Imagine 2.0 T2I (xAI via FAL): rate $0.0667-$0.1333/image (50% GM on FAL $0.04-$0.08 by resolution × quality)",
    calculateCost: (rate, params) => calculateGrokImagine2Cost(rate, params, 0),
    providerCosts: {
      fal: {
        rate: getGrokImagine2FalRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.04 (1k/low), $0.06 (1k/medium), $0.06 (2k/low), $0.08 (2k/medium) per image",
        calculateCost: (rate, params) => calculateGrokImagine2Cost(rate, params, 0),
      },
    },
  },

  // Grok Imagine Image 2.0 Edit (FAL: xai/grok-imagine-image/v2.0/edit) — same per-output grid,
  // plus $0.01 per reference image (1-3). The surcharge is marked up alongside the base rate so
  // margin holds on multi-reference edits.
  [GrokImageGenerationModelType.GROK_IMAGINE_2_EDIT]: {
    rate: getGrokImagine2Rate,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "Grok Imagine 2.0 Edit (xAI via FAL): rate $0.0667-$0.1333/image + $0.0167 per reference (50% GM on FAL $0.04-$0.08 + $0.01/reference)",
    calculateCost: (rate, params) =>
      calculateGrokImagine2Cost(rate, params, GROK_2_REFERENCE_SURCHARGE_USD * GROK_2_MARGIN_MULTIPLIER),
    providerCosts: {
      fal: {
        rate: getGrokImagine2FalRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: $0.04-$0.08/image by resolution × quality, plus $0.01 per input image (1-3 references)",
        calculateCost: (rate, params) => calculateGrokImagine2Cost(rate, params, GROK_2_REFERENCE_SURCHARGE_USD),
      },
    },
  },

  // Reve 2.1 T2I (Replicate: reve/reve-2.1 $0.20 official, FAL: reve/2.1/text-to-image $0.25) — strong prompt adherence + text rendering
  [EReveImageGenerationModelType.REVE_2_1]: {
    rate: 0.25,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "Reve 2.1: $0.20/image (Replicate official) or $0.25/image (FAL) — T2I, layout intelligence + text rendering",
    providerCosts: {
      replicate: {
        rate: 0.2,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.20/image (official, 20% cheaper than FAL)",
      },
      fal: { rate: 0.25, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.25/image" },
    },
  },

  // Reve 2.1 Edit (Replicate: reve/reve-2.1 $0.20 official, FAL: reve/2.1/edit $0.25) — single-reference edit
  [EReveImageGenerationModelType.REVE_2_1_EDIT]: {
    rate: 0.25,
    unit: EPricingUnit.PER_IMAGE,
    description: "Reve 2.1 Edit: $0.20/image (Replicate official) or $0.25/image (FAL) — single-reference image edit",
    providerCosts: {
      replicate: {
        rate: 0.2,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.20/image (official, 20% cheaper than FAL)",
      },
      fal: { rate: 0.25, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.25/image" },
    },
  },

  // Reve 2.1 Remix (Replicate: reve/reve-2.1 $0.20 official, FAL: reve/2.1/remix $0.25) — multi-reference remix (1-8 images)
  [EReveImageGenerationModelType.REVE_2_1_REMIX]: {
    rate: 0.25,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "Reve 2.1 Remix: $0.20/image (Replicate official) or $0.25/image (FAL) — multi-reference remix, 1-8 images",
    providerCosts: {
      replicate: {
        rate: 0.2,
        unit: EPricingUnit.PER_IMAGE,
        description: "Replicate: $0.20/image (official, 20% cheaper than FAL)",
      },
      fal: { rate: 0.25, unit: EPricingUnit.PER_IMAGE, description: "FAL: $0.25/image" },
    },
  },

  // MAI Image 2.5 Pro T2I (FAL only: microsoft/mai-image-2.5-pro) — token-billed
  // (text $7.50/1M tok + image-output $162/1M tok); FAL quotes "~$0.17/image average".
  // rate sits above the average for headroom on long prompts / large outputs.
  [EKreaImageGenerationModelType.KREA_3]: {
    rate: getKreaWaveRate,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "Krea 3 WaveSpeed: USD0.03/1024² output pixels; each edit reference at output size, ceil total microUSD",
    providerCosts: {
      wavespeed: {
        rate: getKreaWaveRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "WaveSpeed published output-size/reference formula; not invoice truth",
      },
    },
  },
  [EKreaImageGenerationModelType.KREA_3_EDIT]: {
    rate: getKreaEditWaveRate,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "Krea 3 WaveSpeed: USD0.03/1024² output pixels; each edit reference at output size, ceil total microUSD",
    providerCosts: {
      wavespeed: {
        rate: getKreaEditWaveRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "WaveSpeed published output-size/reference formula; not invoice truth",
      },
    },
  },
  [EMossImageGenerationModelType.MOSS_IMAGE]: {
    rate: getMossWaveRate,
    unit: EPricingUnit.PER_IMAGE,
    description: "MOSS Image WaveSpeed: $0.02/1K, $0.04/1.5K or $0.08/2K per image",
    providerCosts: {
      wavespeed: {
        rate: getMossWaveRate,
        unit: EPricingUnit.PER_IMAGE,
        description: "WaveSpeed published flat tariff, not an invoice",
      },
    },
  },
  [EMaiImageGenerationModelType.MAI_IMAGE_2_5_PRO]: {
    rate: 0.18,
    unit: EPricingUnit.PER_IMAGE,
    description: "MAI Image 2.5 Pro: ~$0.17/image (FAL average, token-billed) — text-to-image",
    providerCosts: {
      fal: { rate: 0.17, unit: EPricingUnit.PER_IMAGE, description: "FAL: ~$0.17/image average (token-billed)" },
    },
  },

  // MAI Image 2.5 Pro Edit (FAL only: microsoft/mai-image-2.5-pro/edit) — token-billed; FAL quotes a
  // RANGE ($0.18-$0.27/image) since input-image size drives image-input token cost. Consumer rate uses
  // the CEILING so the margin floor holds at any input size; providerCosts carries the midpoint.
  [EMaiImageGenerationModelType.MAI_IMAGE_2_5_PRO_EDIT]: {
    rate: 0.27,
    unit: EPricingUnit.PER_IMAGE,
    description:
      "MAI Image 2.5 Pro Edit: $0.18-$0.27/image (FAL, token-billed, varies with input image size) — priced at ceiling",
    providerCosts: {
      fal: {
        rate: 0.225,
        unit: EPricingUnit.PER_IMAGE,
        description: "FAL: ~$0.225/image (midpoint of documented $0.18-$0.27 range)",
      },
    },
  },
}

/**
 * Calculate image generation credits based on model and parameters
 * Uses the pricing configuration to determine costs
 * TODO: Implement with actual Replicate pricing data
 */

export function calculateImageCredits(modelId: ImageGenerationModelType, params: ImageGenerationModelParams): number {
  const pricing = replicateImageModelPricing[normalizeImageModelId(modelId)]

  if (!pricing) {
    blg.warn(`Unknown image model for credit calculation: ${modelId}, using default rate`)
    return 0.01 // default: $0.01 per image
  }

  // Get the rate (handles both number and function)
  const rate = typeof pricing.rate === "function" ? pricing.rate(params) : pricing.rate

  // Use custom calculation if provided
  if (pricing.calculateCost) {
    return pricing.calculateCost(rate, params)
  }

  // Fall back to standard calculation based on unit type
  switch (pricing.unit) {
    case EPricingUnit.PER_IMAGE:
      return calculatePerImage(rate, params)

    case EPricingUnit.PER_REQUEST:
      return rate

    case EPricingUnit.PER_SECOND:
    case EPricingUnit.PER_FRAME:
      return rate // image models don't use per-second/per-frame, fallback to rate

    // Token/character units belong to the LLM and TTS registries in `modalityPricing.ts`;
    // an image/video model can never carry them. Enumerated so the switch stays exhaustive.
    case EPricingUnit.PER_TOKEN:
    case EPricingUnit.PER_CHARACTER:
      return rate
  }
}

/**
 * Calculate video generation credits based on model and parameters
 * Uses the pricing configuration to determine costs
 */

export function calculateImageGenerationCredits(
  modelId: ImageGenerationModelType,
  params: ImageGenerationModelParams,
): number {
  const internalCost = calculateImageCredits(modelId, params)
  return convertUsdToCredits(internalCost)
}

// ============================================================================
// Provider Cost Utilities (for margin tracking)
// ============================================================================

/**
 * Calculate the actual provider cost for a video generation request.
 * Returns null if the model or provider has no pricing data.
 */

export function getImageProviderCost(
  modelId: ImageGenerationModelType,
  provider: string,
  params: ImageGenerationModelParams,
): number | null {
  const pricing = replicateImageModelPricing[normalizeImageModelId(modelId)]
  if (!pricing?.providerCosts?.[provider]) return null

  const entry = pricing.providerCosts[provider]
  const rate = typeof entry.rate === "function" ? entry.rate(params) : entry.rate

  if (entry.calculateCost) {
    return entry.calculateCost(rate, params)
  }

  switch (entry.unit) {
    case EPricingUnit.PER_IMAGE:
      return calculatePerImage(rate, params)
    case EPricingUnit.PER_REQUEST:
      return rate
    case EPricingUnit.PER_SECOND:
    case EPricingUnit.PER_FRAME:
      return rate // image provider costs don't use per-second/per-frame, return flat rate

    // Token/character units belong to the LLM and TTS registries in `modalityPricing.ts`;
    // an image/video model can never carry them. Enumerated so the switch stays exhaustive.
    case EPricingUnit.PER_TOKEN:
    case EPricingUnit.PER_CHARACTER:
      return rate
  }
}

/**
 * The unit a provider bills this image call in, plus the billable QUANTITY in that unit.
 *
 * Image sibling of `getVideoProviderBilling` — same contract, same reason: the unit is the
 * registry's declared `providerCosts[provider].unit` and the quantity reuses the same param helper
 * at rate = 1, so unit, quantity and dollars cannot drift apart. See VM-1012.
 */
export function getImageProviderBilling(
  modelId: ImageGenerationModelType,
  provider: string,
  params: ImageGenerationModelParams,
): { unit: EPricingUnit; quantity: number } | null {
  const pricing = replicateImageModelPricing[normalizeImageModelId(modelId)]
  if (!pricing?.providerCosts?.[provider]) return null

  const entry = pricing.providerCosts[provider]

  switch (entry.unit) {
    case EPricingUnit.PER_IMAGE:
      return { unit: EPricingUnit.PER_IMAGE, quantity: calculatePerImage(1, params) }
    // Flat-priced call — mirrors the branches above that return the bare rate.
    case EPricingUnit.PER_REQUEST:
    case EPricingUnit.PER_SECOND:
    case EPricingUnit.PER_FRAME:
    case EPricingUnit.PER_TOKEN:
    case EPricingUnit.PER_CHARACTER:
      return { unit: EPricingUnit.PER_REQUEST, quantity: 1 }
  }
}

/**
 * Compute margin data from consumer cost and provider cost.
 * Used for Axiom event logging.
 */
